import React, { useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useMatchMedia from 'hooks/useMatchMedia';
import useCallbackRef from 'hooks/useCallbackRef';

import './ProgressStepper.styles.scss';
import './ProgressStepper.styles.responsive.scss';

export type ProgressStepperStep = {
  title: string;
  onClick: () => void;
};

type ProgressStepperProps = {
  steps: ProgressStepperStep[];
  currentStep: number;
  className?: string;
};

const ProgressStepper: React.FC<ProgressStepperProps> = (props) => {
  const { className, steps, currentStep } = props;

  const isPhablet = useMatchMedia('isPhablet');
  const { t } = useTranslation();
  const [container, containerRef] = useCallbackRef();
  const firstElement = useRef<HTMLElement>();
  const lastElement = useRef<HTMLElement>();

  const classes = classNames('progress-stepper', className);

  const translateWidthPercentage = useMemo(
    () => 100 / (isPhablet ? steps.length : steps.length - 1),
    [isPhablet, steps.length],
  );

  const renderSteps = useMemo(
    () =>
      !!steps.length &&
      steps.map(({ title, onClick }, index) => (
        <li key={title}>
          <div
            ref={(ref) => {
              if (index === 0) {
                firstElement.current = ref;
              } else if (index === steps.length - 1) {
                lastElement.current = ref;
              }
            }}
            className={classNames({
              completed: currentStep > index,
              previous: currentStep > index,
              active: currentStep === index,
              disabled: currentStep < index,
            })}
            onClick={onClick}
          >
            <span>{index + 1}</span>
            <span className="popup">{t(title)}</span>
          </div>
        </li>
      )),
    [currentStep, steps, t],
  );

  useEffect(() => {
    if (!container || !firstElement.current) return;

    container.style.setProperty(
      '--left-progress',
      `${firstElement.current.clientWidth / 2}px`,
    );
    container.style.setProperty(
      '--right-progress',
      `${lastElement.current.clientWidth / 2}px`,
    );
  }, [container]);

  return (
    <nav className={classes} ref={containerRef}>
      <div>
        <div className="progress-stepper__progress">
          <div
            style={{
              transform: `translate3d(-${
                100 - translateWidthPercentage * currentStep
              }%,0,0)`,
            }}
          />
          <div
            style={{
              transform: `translate3d(-${
                100 -
                translateWidthPercentage *
                  (isPhablet ? currentStep + 1 : currentStep)
              }%,0,0)`,
            }}
          />
        </div>
        <ul>{renderSteps}</ul>
      </div>
    </nav>
  );
};

export default ProgressStepper;
